import React, { FC, forwardRef, HTMLProps, PropsWithChildren, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';
import theme, { combineValues } from '@cld/theme-helper';
import { asStyledComponent } from '@cld/component-enhancer';
import { mergeCallbacks } from '../../utils/mergeCallbacks';
import { useListVariant } from './ListVariantProvider';
import { useListSelection } from './ListSelectionProvider';
import { IconTwoLineLayout, TextItemLayout } from './item-layout';

const t = theme({ basePath: 'components.compound-components.listItem' });

type SupportedHTMLProps = Pick<HTMLProps<HTMLElement>, 'role'>;

export type ListItemTextMode = 'default' | 'error' | 'success' | 'warning';
export type ListItemSelectionVariant = 'solid' | 'subtle';

export interface ListItemProps extends SupportedHTMLProps {
  /** show selected state */
  selected?: boolean;
  /** show focused state */
  focused?: boolean;
  /** use hover style on css :hover state */
  hoverable?: boolean;
  /** fired on item click */
  onClick?: () => void;
  /** add visible border to item to be visually separated from upper item */
  striped?: boolean;
  /** makes item rounded corners for first and last item */
  rounded?: boolean;
  /** meaningful style of text (error, success, warning, ...) */
  textMode?: ListItemTextMode;
  /** variant of selected state. Design System supports more than one */
  selectionVariant?: ListItemSelectionVariant;
}

const hoverStyle = css`
   background: ${combineValues(
  t`selectionVariant.${'selectionVariant'}.hover.color`,
  t`selectionVariant.${'selectionVariant'}.hover.opacity`,
  (color, opacity) => rgba(color, opacity)
)};
`;

const Root = styled.div<ListItemProps>`
  cursor: pointer;
  
  ${(props) =>
    props.selected
      ? css`
          background: ${combineValues(
            t`selectionVariant.${'selectionVariant'}.background.color`,
            t`selectionVariant.${'selectionVariant'}.background.opacity`,
            (color, opacity) => rgba(color, opacity)
          )};
        `
      : props.focused
      ? hoverStyle
      : props.hoverable &&
        css`
          &:hover {
            ${hoverStyle};
          }
        `};

  ${(props) =>
    props.striped &&
    css`
      &:not(:first-child) {
        border-top: 1px solid ${theme`palette.background`};
      }
    `};

  ${(props) =>
    props.rounded &&
    css`
      &:first-of-type {
        border-top-left-radius: ${theme`spacing.xxs`};
        border-top-right-radius: ${theme`spacing.xxs`};
      }
      &:last-of-type {
        border-bottom-left-radius: ${theme`spacing.xxs`};
        border-bottom-right-radius: ${theme`spacing.xxs`};
      }
    `};
`;

Root.defaultProps = {
  textMode: 'default',
  selectionVariant: 'solid',
  hoverable: true,
};

/**
 * List item build primitive. It is responsible to style the item itself. The content can be anything.
 */
export const ListItemPure = forwardRef<HTMLDivElement, PropsWithChildren<ListItemProps>>(({ children, ...rest }, ref) => {
  return (
    <Root role="listitem" {...rest} ref={ref}>
      {children}
    </Root>
  );
});

const useListItemProps = (id: string, { role = 'listitem', onClick, ...rest }: ListItemProps) => {
  const listContext = useListVariant();
  const { onClick: useListSelectionOnClick, ...selectionContext } = useListSelection(id);

  return { 'data-id': id, role, ...listContext, ...selectionContext, ...rest, onClick: mergeCallbacks(onClick, useListSelectionOnClick) };
};

const DefaultListItem = asStyledComponent<FC<ListItemProps & { id: string }>>(({ id, children, ...rest }) => {
  const props = useListItemProps(id, rest);
  return <ListItemPure {...props}>{children}</ListItemPure>;
});

const TextListItem = asStyledComponent<FC<ListItemProps & { id: string }>>(({ id, children, ...rest }) => {
  const props = useListItemProps(id, rest);
  return (
    <ListItemPure {...props}>
      <TextItemLayout isSelected={props.selected} selectionVariant={props.selectionVariant} showCheckIconWhenSelected={props.showCheckIconWhenSelected}>
        {children}
      </TextItemLayout>
    </ListItemPure>
  );
});

const IconTwoLineItem = asStyledComponent<
  FC<ListItemProps & { id: string } & { icon?: ReactNode; primaryText?: ReactNode; secondaryText?: ReactNode; actions?: ReactNode }>
>(({ id, children, icon, primaryText, secondaryText, actions, ...rest }) => {
  const props = useListItemProps(id, rest);
  return (
    <ListItemPure {...props}>
      <IconTwoLineLayout
        isSelected={props.selected}
        selectionVariant={props.selectionVariant}
        showCheckIconWhenSelected={props.showCheckIconWhenSelected}
        {...{ icon, primaryText, secondaryText, actions }}
      />
    </ListItemPure>
  );
});
export const ListItem = {
  Default: DefaultListItem,
  Text: TextListItem,
  IconTwoLine: IconTwoLineItem,
};
